import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, initials } from "@/context/AuthContext";
import AuthDialog from "./AuthDialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { User, Settings, FileText, LogOut, ChevronDown, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";

export default function ProfileMenu({ variant = "desktop" }) {
  const { user, logout } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState("login");
  const navigate = useNavigate();

  const openLogin = () => { setDefaultTab("login"); setDialogOpen(true); };
  const openSignup = () => { setDefaultTab("signup"); setDialogOpen(true); };

  if (!user) {
    if (variant === "mobile") {
      return (
        <>
          <div className="flex gap-3 pt-3 border-t border-[#E2E8F0]">
            <button onClick={openLogin} className="flex-1 btn-secondary text-xs py-3" data-testid="mobile-signin-btn">Sign In</button>
            <button onClick={openSignup} className="flex-1 btn-primary text-xs py-3" data-testid="mobile-signup-btn">Sign Up</button>
          </div>
          <AuthDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultTab={defaultTab} />
        </>
      );
    }
    return (
      <>
        <button
          onClick={openLogin}
          data-testid="signin-btn"
          className="flex items-center gap-2 text-sm font-medium text-[#0B2341] hover:text-[#16A34A] transition-colors px-3 py-2"
        >
          <User size={16} />
          <span className="hidden xl:inline">Sign In</span>
        </button>
        <AuthDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultTab={defaultTab} />
      </>
    );
  }

  const handleLogout = () => {
    logout();
    toast.success("Signed out.");
    navigate("/");
  };

  if (variant === "mobile") {
    return (
      <div className="pt-3 border-t border-[#E2E8F0] space-y-2" data-testid="mobile-profile-section">
        <div className="flex items-center gap-3 py-2">
          <Avatar name={user.name} />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm font-semibold text-[#0B2341] truncate">{user.name}</div>
            <div className="text-xs text-[#4A5568] truncate">{user.email}</div>
          </div>
        </div>
        <Link to="/profile" data-testid="mobile-profile-link" className="block py-2 text-sm text-[#0B2341]">My Profile</Link>
        <Link to="/profile?tab=settings" data-testid="mobile-settings-link" className="block py-2 text-sm text-[#0B2341]">Account Settings</Link>
        <button onClick={handleLogout} data-testid="mobile-logout" className="block w-full text-left py-2 text-sm text-[#16A34A]">Sign Out</button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="profile-trigger"
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#F8F9FA] transition-colors rounded-none focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
        >
          <Avatar name={user.name} size="sm" />
          <span className="hidden xl:flex items-center gap-1 text-sm font-medium text-[#0B2341]">
            <span className="max-w-[110px] truncate">{user.name.split(" ")[0]}</span>
            <ChevronDown size={14} className="text-[#4A5568]" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-64 rounded-none border-[#E2E8F0] p-0 shadow-xl"
        data-testid="profile-dropdown"
      >
        <div className="px-4 py-4 bg-[#0B2341] text-white">
          <div className="flex items-center gap-3">
            <Avatar name={user.name} size="md" inverse />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-semibold truncate">{user.name}</div>
              <div className="text-xs text-white/70 truncate">{user.email}</div>
            </div>
          </div>
          {user.company && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#16A34A] font-medium truncate">
              {user.company}
            </div>
          )}
        </div>

        <DropdownMenuLabel className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#4A5568] font-bold">
          Account
        </DropdownMenuLabel>

        <DropdownMenuItem asChild>
          <Link to="/profile" data-testid="menu-profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B2341] hover:bg-[#F8F9FA] cursor-pointer">
            <LayoutDashboard size={15} className="text-[#16A34A]" />
            My Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/profile?tab=enquiries" data-testid="menu-enquiries" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B2341] hover:bg-[#F8F9FA] cursor-pointer">
            <FileText size={15} className="text-[#16A34A]" />
            My Enquiries
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/profile?tab=settings" data-testid="menu-settings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B2341] hover:bg-[#F8F9FA] cursor-pointer">
            <Settings size={15} className="text-[#16A34A]" />
            Account Settings
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-[#E2E8F0]" />

        <DropdownMenuItem
          onClick={handleLogout}
          data-testid="menu-logout"
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#16A34A] hover:bg-[#F8F9FA] cursor-pointer focus:text-[#16A34A]"
        >
          <LogOut size={15} />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Avatar({ name, size = "sm", inverse = false }) {
  const cls = size === "md" ? "w-10 h-10 text-sm" : "w-9 h-9 text-xs";
  return (
    <div
      data-testid="user-avatar"
      className={`${cls} flex items-center justify-center font-heading font-semibold flex-shrink-0 ${
        inverse ? "bg-[#16A34A] text-white" : "bg-[#0B2341] text-white"
      }`}
    >
      {initials(name)}
    </div>
  );
}
